import {CurrencyPipe, NgClass} from '@angular/common';
import {Component, ViewEncapsulation} from '@angular/core';
import {ToastrService, Toast, ToastPackage} from 'ngx-toastr';
import {TranslocoService} from '@jsverse/transloco';
import {finalize} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {DateTime} from 'luxon';
import {PendingBooking} from '../../../entities/booking/pending-booking.entity';

export interface PendingBookingToastPayload {
  booking: PendingBooking;
  actions: {
    learnMore: () => Observable<unknown>;
    refuse: () => Observable<unknown>;
    accept: () => Observable<unknown>;
  };
}

@Component({
  selector: 'app-pending-booking-toast',
  standalone: true,
  imports: [],
  providers: [CurrencyPipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './pending-booking-toast.component.html',
  styleUrl: './pending-booking-toast.component.scss'
})
export class PendingBookingToastComponent extends Toast<PendingBookingToastPayload> {
  public readonly booking: PendingBooking|null;
  public readonly bodyText: string;
  public readonly learnMoreLabel: string;
  public readonly refuseLabel: string;
  public readonly acceptLabel: string;
  public readonly serviceTitle: string;
  public readonly closeLabel: string;
  public actionInProgress = false;

  public constructor(
    toastrService: ToastrService,
    toastPackage: ToastPackage<PendingBookingToastPayload>,
    private readonly transloco: TranslocoService,
    private readonly currencyPipe: CurrencyPipe,
  ) {
    super(toastrService, toastPackage);
    this.booking = toastPackage.config.payload?.booking ?? null;
    this.serviceTitle = this.truncateServiceName(this.booking?.serviceName ?? '');
    this.learnMoreLabel = this.transloco.translate('sidebar.bookings.pending-approval-toast.actions.learn-more');
    this.refuseLabel = this.transloco.translate('sidebar.bookings.pending-approval-toast.actions.refuse');
    this.acceptLabel = this.transloco.translate('sidebar.bookings.pending-approval-toast.actions.accept');
    this.closeLabel = this.transloco.translate('sidebar.bookings.pending-approval-toast.actions.close');
    this.bodyText = this.buildBodyText();
  }

  public onLearnMore(): void {
    this.runAction(this.toastPackage.config.payload?.actions.learnMore);
  }

  public onRefuse(): void {
    this.runAction(this.toastPackage.config.payload?.actions.refuse);
  }

  public onAccept(): void {
    this.runAction(this.toastPackage.config.payload?.actions.accept);
  }

  private runAction(action?: () => Observable<unknown>): void {
    if (!action || this.actionInProgress) {
      return;
    }

    this.actionInProgress = true;
    action().pipe(
      finalize(() => this.actionInProgress = false)
    ).subscribe({
      next: () => this.remove(),
      error: () => {},
    });
  }

  private buildBodyText(): string {
    const dateLabel = this.booking?.datetime
      ? this.booking.datetime.toLocaleString(DateTime.DATETIME_MED)
      : this.transloco.translate('sidebar.bookings.pending-approval-toast.no-date');
    const priceLabel = this.currencyPipe.transform(this.booking?.price ?? 0, 'EUR') ?? '';

    return this.transloco.translate('sidebar.bookings.pending-approval-toast.message', {
      date: dateLabel,
      price: priceLabel
    });
  }

  private truncateServiceName(name: string, limit = 52): string {
    if (name.length <= limit) {
      return name;
    }

    return `${name.slice(0, limit - 3)}...`;
  }
}
